import { PriorityTiny } from '../../entities/job/priority.tiny';
import { JobMode } from '../../entities/enums';
import { Entity } from '../../entities/entity';
import { Address } from '../../entities/shared/address';
import { ContactSummary } from '../../entities/contact/contact';
import { SystemWorkOrderStatus } from '../../entities/job/system.workorder.status';
import { JobType } from '../../entities/tenant/job.type';
import { EntityLink } from '../../entities/entity';
import { JobPolicyThresholds } from '../../entities/policies/job.policy.thresholds';
import { Site, SiteTiny } from '../../entities/shared/site';

export interface JobPatchRequest {
    jobId: string;
    workOrderId: string;
    name?: string;
    status?: SystemWorkOrderStatus;
    priority?: PriorityTiny;
    dueDate?: Date;
    startDate?: Date;
    contact?: ContactSummary;
    description?: string;
    technician?: Entity;
    type?: JobType;
    customerPO?: string;
    customerWO?: string;
    owner?: ContactSummary;
    supervisor?: ContactSummary;
    jobDueDate?: Date;
    jobStartDate?: Date;
    siteAddress?: Address;
    pricebook?: Entity;
    completedDate?: Date;
    completedDatePrevious?: Date;
    finalisedDate?: Date;
    finalisedDatePrevious?: Date;
    updatedAt?: string;
}

export interface JobPatchField {
    priority?: PriorityTiny;
    contact?: ContactSummary;
    description?: string;
    type?: JobType;
    customerPO?: string;
    customerWO?: string;
    owner?: ContactSummary;
    fieldSupervisor?: ContactSummary;
    supervisor?: ContactSummary;
    site?: SiteTiny;
    customerSite?: SiteTiny;
    pricebook?: Entity;
    labels?: Entity[];
    dueDate?: Date;
    startDate?: Date;
    customFields?: any;
    zones?: Entity[];
    links?: EntityLink[];
    mode?: JobMode;
}

export enum JobUpdatedField {
    Contact = 'contact',
    Description = 'description',
    Priority = 'priority',
    Supervisor = 'supervisor',
    CustomerWO = 'customerWO',
    CustomerPO = 'customerPO',
    DueDate = 'dueDate',
    StartDate = 'startDate',
    Owner = 'owner',
    FieldSupervisor = 'fieldSupervisor',
    Pricebook = 'pricebook',
    Type = 'type',
    Labels = 'labels',
    Site = 'site',
    CustomerSite = 'customerSite',
    BillingCompany = 'billingCompany',
    Address = 'address',
    PlaceId = 'placeId',
    CustomFields = 'customFields',
    zones = 'zones',
    Claim = 'claim',
    DefectCount = 'defectCount',
    Links = 'links',
}

export const JobAllowUpdateFields = [
    'contact',
    'description',
    'priority',
    'supervisor',
    'customerWO',
    'customerPO',
    'dueDate',
    'startDate',
    'owner',
    'pricebook',
    'billingCompany',
    'type',
    'labels',
    'site',
    // The customer's own address. Deliberately not routed through the 'site' case in JobPatchTask:
    // that branch recomputes the job's zones from the address, and the customer's address must not
    // move the job's zones.
    'customerSite',
    'address',
    'fieldSupervisor',
    'placeId',
    'customFields',
    'zones',
    'claim',
    'defectCount',
    'thresholds',
    'workorderDispatchTheme',
    'links',
    'policy',
    'integrationFields',
    'name',
    // Job structure transitions (Basic → Advanced/Project, Advanced → Project). The one-way ladder, the
    // addon entitlement and the Change Job Structure right are enforced by JobStructureTransitionValidator
    // (patch pipeline and CQRS patch handler) — a violating mode patch is rejected, not dropped.
    'mode',
];


// this is cool but can not use, just protect at compile time, run time we will die
// export type JobSafePatch = { [K in keyof Partial<JobPatchField>]: any };

/**
 * Primary-contact payload of a job patch. `isAdhocContact` marks a contact the job stores itself
 * rather than a CRM record: the `_id` is then the job-local id of that contact's own contact link,
 * not a CRM contact id, and the pipeline must reuse it instead of reading it as a re-link.
 */
export interface JobPatchContact extends ContactSummary {
    isAdhocContact?: boolean;
}

export interface JobPipelinePatchRequest {
    jobId: string;
    updatedAt?: string;
    name?: string;
    priority?: PriorityTiny;
    contact?: JobPatchContact;
    description?: string;
    type?: JobType;
    customerPO?: string;
    customerWO?: string;
    owner?: ContactSummary;
    fieldSupervisor?: ContactSummary;
    supervisor?: ContactSummary;
    pricebook?: Entity;
    billingCompany?: Entity;
    //Should only be set with pricebook, as it will only be used to override the default company of the pricebook
    billingCompanyOverride?: Entity;
    labels?: Entity[];
    dueDate?: Date;
    startDate?: Date;
    site?: Site;
    /** The customer's own address; carries only the address half, the contact stays in `supervisor`. */
    customerSite?: SiteTiny;
    address?: Address;
    placeId?: string;
    zones?: Entity[];
    defectCount?: number;
    thresholds?: JobPolicyThresholds;
    policy?: Entity;
    workorderDispatchTheme?: Entity;
    customFields?: any;
    integrationFields?: any;
    links?: EntityLink[];
    /** One-way job structure upgrade; validated against the job's current mode in the pipeline. */
    mode?: JobMode;

    /** to know what kind of action, currently it is used for "Update Pricing" */
    action?: 'Update Pricing';
}

export interface JobLabelAddedRequest {
    value: string;
    field: string;
    labelName: string;
    message?: string;
}
